import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Download, Filter, Search, Calendar, DollarSign, CreditCard, AlertTriangle, CheckCircle, Plus, X } from 'lucide-react';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { useApi } from '../hooks/useApi';
import { enhancedAPI, paymentAPI } from '../services/api';

const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Helper function to format payment methods
const formatPaymentMethod = (method) => {
  if (!method || method === '-') return '-';
  
  const methodMap = {
    'manual': 'Manual',
    'bank_transfer': 'Bank Transfer',
    'stripe': 'Credit Card',
    'paypal': 'PayPal',
    'credit_card': 'Credit Card',
    'debit_card': 'Debit Card',
    'cash': 'Cash',
    'check': 'Check',
    'wire_transfer': 'Wire Transfer',
    'autopay': 'Autopay'
  };
  
  return methodMap[method.toLowerCase()] || method.charAt(0).toUpperCase() + method.slice(1);
};

const Payments = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  const [manualPaymentData, setManualPaymentData] = useState({
    tenant_id: '',
    amount: '',
    payment_method: 'check',
    payment_date: new Date().toISOString().split('T')[0],
    months_paid: [],
    transaction_reference: '',
    notes: '',
    status: 'paid'
  });

  const { data: payments, loading, error, refetch } = useApi(() => enhancedAPI.getPaymentsWithDetails());
  const { data: tenants } = useApi(() => enhancedAPI.getTenantsWithProperties());

  const filteredPayments = payments ? payments.filter(payment => {
    const matchesSearch = (payment.tenantName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (payment.propertyName || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || payment.status === filterStatus;
    return matchesSearch && matchesFilter;
  }) : [];

  const handleManualPaymentSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate required fields
      if (!manualPaymentData.tenant_id || !manualPaymentData.amount) {
        alert('Please select a tenant and enter an amount');
        return;
      }

      // Prepare payment data
      const paymentData = {
        ...manualPaymentData,
        amount: parseFloat(manualPaymentData.amount),
        payment_type: 'manual',
        months_paid: manualPaymentData.months_paid.length > 0 ? manualPaymentData.months_paid : [new Date().getMonth() + 1]
      };

      await paymentAPI.create(paymentData);
      
      // Reset form and close modal
      setManualPaymentData({
        tenant_id: '',
        amount: '',
        payment_method: 'check',
        payment_date: new Date().toISOString().split('T')[0],
        months_paid: [],
        transaction_reference: '',
        notes: '',
        status: 'paid'
      });
      setShowManualPaymentModal(false);
      
      // Refresh payments list
      refetch();
      
    } catch (error) {
      console.error('Error creating manual payment:', error);
      alert('Failed to create manual payment. Please try again.');
    }
  };

  const PaymentRow = ({ payment }) => (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.tenantName}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.propertyName}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.unitNumber}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${payment.amount?.toFixed(2)}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {formatPaymentMethod(payment.method)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        <Badge 
          className={
            payment.status === 'paid' ? 'bg-green-100 text-green-800' :
            payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
            payment.status === 'overdue' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }
        >
          {payment.status}
        </Badge>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.transaction_id || '-'}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.paid_date ? new Date(payment.paid_date).toLocaleString() : '-'}</td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {Array.isArray(payment.months) && payment.months.length > 0 ? payment.months.map(m => monthNames[(m-1)] || m).join(', ') : '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
        {payment.partial === true ? 'Yes' : payment.partial === false ? 'No' : '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex space-x-2">
          <Button size="sm" variant="outline">
            View
          </Button>
          {payment.status === 'pending' && (
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={async ()=>{
              await paymentAPI.update(payment.id, { status: 'paid' });
              refetch();
            }}>
              Mark Paid
            </Button>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payments</h1>
          <p className="text-gray-600 mt-1">Review and manage tenant payments</p>
        </div>
        <Button 
          onClick={() => setShowManualPaymentModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Manual Payment
        </Button>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by tenant or property..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button variant={filterStatus === 'all' ? 'default' : 'outline'} onClick={() => setFilterStatus('all')}>All</Button>
              <Button variant={filterStatus === 'paid' ? 'default' : 'outline'} onClick={() => setFilterStatus('paid')}>Paid</Button>
              <Button variant={filterStatus === 'pending' ? 'default' : 'outline'} onClick={() => setFilterStatus('pending')}>Pending</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>All Payments</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Months</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Partial</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPayments.map((payment) => (
                  <PaymentRow key={payment.id} payment={payment} />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Manual Payment Modal */}
      {showManualPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Add Manual Payment</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowManualPaymentModal(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <form onSubmit={handleManualPaymentSubmit} className="p-6 space-y-4">
              {/* Tenant Selection */}
              <div>
                <Label htmlFor="tenant_id" className="text-sm font-medium">Tenant *</Label>
                <Select 
                  value={manualPaymentData.tenant_id} 
                  onValueChange={(value) => setManualPaymentData({...manualPaymentData, tenant_id: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tenant..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants?.map((tenant) => (
                      <SelectItem key={tenant.id} value={tenant.id}>
                        {tenant.name} - {tenant.propertyName} ({tenant.unit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Payment Amount */}
                <div>
                  <Label htmlFor="amount" className="text-sm font-medium">Payment Amount *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={manualPaymentData.amount}
                      onChange={(e) => setManualPaymentData({...manualPaymentData, amount: e.target.value})}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Payment Date */}
                <div>
                  <Label htmlFor="payment_date" className="text-sm font-medium">Payment Date *</Label>
                  <Input
                    id="payment_date"
                    type="date"
                    value={manualPaymentData.payment_date}
                    onChange={(e) => setManualPaymentData({...manualPaymentData, payment_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Payment Method */}
                <div>
                  <Label htmlFor="payment_method" className="text-sm font-medium">Payment Method</Label>
                  <Select 
                    value={manualPaymentData.payment_method} 
                    onValueChange={(value) => setManualPaymentData({...manualPaymentData, payment_method: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="wire_transfer">Wire Transfer</SelectItem>
                      <SelectItem value="money_order">Money Order</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Payment Status */}
                <div>
                  <Label htmlFor="status" className="text-sm font-medium">Status</Label>
                  <Select 
                    value={manualPaymentData.status} 
                    onValueChange={(value) => setManualPaymentData({...manualPaymentData, status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="processing">Processing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Transaction Reference */}
              <div>
                <Label htmlFor="transaction_reference" className="text-sm font-medium">Transaction Reference</Label>
                <Input
                  id="transaction_reference"
                  placeholder="Check number, transaction ID, etc."
                  value={manualPaymentData.transaction_reference}
                  onChange={(e) => setManualPaymentData({...manualPaymentData, transaction_reference: e.target.value})}
                />
              </div>

              {/* Months Paid For */}
              <div>
                <Label className="text-sm font-medium">Months Paid For</Label>
                <div className="grid grid-cols-6 gap-2 mt-2">
                  {monthNames.map((month, index) => (
                    <label key={month} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={manualPaymentData.months_paid.includes(index + 1)}
                        onChange={(e) => {
                          const monthNum = index + 1;
                          if (e.target.checked) {
                            setManualPaymentData({
                              ...manualPaymentData,
                              months_paid: [...manualPaymentData.months_paid, monthNum]
                            });
                          } else {
                            setManualPaymentData({
                              ...manualPaymentData,
                              months_paid: manualPaymentData.months_paid.filter(m => m !== monthNum)
                            });
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-xs">{month}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes" className="text-sm font-medium">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Additional notes about this payment..."
                  value={manualPaymentData.notes}
                  onChange={(e) => setManualPaymentData({...manualPaymentData, notes: e.target.value})}
                  rows={3}
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowManualPaymentModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Add Payment
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;